import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import toast from 'react-hot-toast'
import EmailCheck from '../EmailCheck'
import { savePromoToBackend } from '../../api/promoClient'
import '../Auth.css'

interface FormData {
  name: string
  email: string
  password: string
  confirmPassword: string
  phone: string
  company: string
  jobTitle: string
  industry: string
  companySize: string
}

export default function SignUp() {
  const [searchParams] = useSearchParams()
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    company: '',
    jobTitle: '',
    industry: 'other',
    companySize: '1-10'
  })
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [passwordStrength, setPasswordStrength] = useState(0)
  const [isEmailAvailable, setIsEmailAvailable] = useState(true)
  const [checkingEmail, setCheckingEmail] = useState(false)
  const [inviteMode, setInviteMode] = useState(false)
  const [inviteToken, setInviteToken] = useState<string | null>(null)
  const [promoCode, setPromoCode] = useState('')
  const [promoApplied, setPromoApplied] = useState(false)
  const { signUp } = useAuth()
  const navigate = useNavigate()

  // ============================================
  // EFFECT: Check for invite token & promo code
  // ============================================
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    
    // Check for invite token
    const token = urlParams.get('invite') || localStorage.getItem('inviteToken')
    if (token) {
      console.log('🎯 Invite token detected:', token)
      setInviteMode(true)
      setInviteToken(token)
      localStorage.setItem('inviteToken', token)
    }
    
    // Check for promo code in URL
    const urlPromo = urlParams.get('promo')
    if (urlPromo) {
      console.log('🎁 Promo code detected:', urlPromo)
      setPromoCode(urlPromo)
      setPromoApplied(true)
      const promoData = {
  code: promoCode,
  discountPercent: 10,    // Changed from 20 to 10
  freeMonths: 3,          // Changed from 2 to 3
  appliedAt: new Date().toISOString(),
  source: 'url_param'
} 
     
      
      localStorage.setItem('appliedPromo', JSON.stringify(promoData))
    }
    
    // Check for promo code in localStorage
    const storedPromo = localStorage.getItem('appliedPromo')
    if (!urlPromo && storedPromo) {
      try {
        const promoData = JSON.parse(storedPromo)
        setPromoCode(promoData.code)
        setPromoApplied(true)
        console.log('🎁 Promo loaded from storage:', promoData.code)
      } catch (e) {
        console.error('Failed to parse stored promo:', e)
      }
    }
  }, [])

  // ============================================
  // EFFECT: Password strength checker
  // ============================================
  useEffect(() => {
    if (formData.password) {
      let strength = 0
      if (formData.password.length >= 8) strength++
      if (/[A-Z]/.test(formData.password)) strength++
      if (/[0-9]/.test(formData.password)) strength++
      if (/[^A-Za-z0-9]/.test(formData.password)) strength++
      setPasswordStrength(strength)
    } else {
      setPasswordStrength(0)
    }
  }, [formData.password])

  // ============================================
  // HANDLER: Save promo to backend
  // ============================================
  const handlePromoSave = async (userData?: any) => {
    const promoStr = localStorage.getItem('appliedPromo')
    
    if (promoStr && userData) {
      try {
        const promoData = JSON.parse(promoStr)
        const userId = userData.id || userData.userId || userData.uid
        
        if (userId) {
          await savePromoToBackend(userId, promoData)
          console.log('✅ Promo saved to backend:', promoData.code)
          
          localStorage.removeItem('appliedPromo')
          setPromoApplied(false)
          setPromoCode('')
        }
      } catch (error) {
        console.error('Failed to save promo to backend:', error)
      }
    }
  }

  // ============================================
  // HANDLER: Handle authentication success
  // ============================================
  const handleAuthSuccess = async () => {
    await handlePromoSave()
    
    const token = localStorage.getItem('inviteToken')
    
    if (token) {
      try {
        const API_URL = import.meta.env.VITE_API_URL || 'https://emailai-backend-658e.onrender.com/api'
        const authToken = localStorage.getItem('accessToken') || localStorage.getItem('token')
        
        const response = await fetch(`${API_URL}/team/accept/${token}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        })
        
        const data = await response.json()
        
        if (data.success) {
          console.log('✅ Invitation accepted!')
          toast.success('🎉 You have joined the team!')
        }
        
        localStorage.removeItem('inviteToken')
        setInviteMode(false)
        setInviteToken(null)
      } catch (error) {
        console.error('Failed to accept invite:', error)
      }
    }
    
    navigate('/dashboard')
  }

  // ============================================
  // HANDLER: Form input changes
  // ============================================
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  // ============================================
  // HANDLER: Form Submit
  // ============================================
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      if (!isEmailAvailable) {
        toast.error('This email is already registered. Please sign in instead.')
        setLoading(false)
        return
      }

      if (formData.password !== formData.confirmPassword) {
        toast.error('Passwords do not match')
        setLoading(false)
        return
      }

      if (passwordStrength < 3) {
        toast.error('Please use a stronger password')
        setLoading(false)
        return
      }

      if (!formData.name.trim()) {
        toast.error('Please enter your full name')
        setLoading(false)
        return
      }

      const result = await signUp({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        phone: formData.phone,
        company: formData.company,
        jobTitle: formData.jobTitle,
        industry: formData.industry,
        companySize: formData.companySize,
        promoCode: promoCode,
        promoApplied: promoApplied
      })

      await handlePromoSave(result)

      // Navigate to verification
      navigate(`/verify-email?email=${encodeURIComponent(formData.email)}${promoApplied ? '&promoApplied=true' : ''}`)
      
      if (promoApplied) {
        toast.success('🎉 Account created! 20% discount applied! Verification code sent!')
      } else {
        toast.success('Account created! Verification code sent to your email!')
      }
    } catch (signupError: any) {
      console.error('❌ Signup error:', signupError)
      toast.error(signupError.message || 'Failed to create account')
    } finally {
      setLoading(false)
    }
  }

  // ============================================
  // HELPERS: Password strength UI
  // ============================================
  const getPasswordStrengthColor = () => {
    switch(passwordStrength) {
      case 0: return 'bg-gray-200'
      case 1: return 'bg-red-500'
      case 2: return 'bg-yellow-500'
      case 3: return 'bg-green-500'
      case 4: return 'bg-green-600'
      default: return 'bg-gray-200'
    }
  }

  const getPasswordStrengthText = () => {
    switch(passwordStrength) {
      case 0: return 'Enter password'
      case 1: return 'Weak'
      case 2: return 'Fair'
      case 3: return 'Good'
      case 4: return 'Strong'
      default: return ''
    }
  }

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="auth-container">
      <Link to="/" className="back-to-landing-button">
        <svg className="back-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Back to Home
      </Link>
      
      <div className="auth-card">
        <div className="auth-header">
          <h1 className="brand-title">EmailBrain</h1>
          <h2 className="auth-title">Create Your Account</h2>
          <p className="auth-subtitle">Join thousands of businesses using AI-powered email marketing</p>
        </div>

        {/* Promo Code Banner */}
        {promoApplied && (
          <div style={{
            padding: '15px',
            background: 'linear-gradient(135deg, #10b981, #059669)',
            color: 'white',
            borderRadius: '8px',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            <p style={{ margin: 0, fontWeight: 'bold' }}>
              🎁 Promo Code Applied!
            </p>
            <p style={{ margin: '5px 0 0', fontSize: '14px' }}>
              You'll get <strong>20% off</strong> for <strong>2 months free!</strong>
            </p>
            <p style={{ margin: '5px 0 0', fontSize: '12px', opacity: 0.9 }}>
              Code: <strong>{promoCode}</strong>
            </p>
          </div>
        )}

        {/* Invite Banner */}
        {inviteMode && (
          <div style={{
            padding: '15px',
            background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
            color: 'white',
            borderRadius: '8px',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            <p style={{ margin: 0, fontWeight: 'bold' }}>🎉 You've been invited to join a team!</p>
            <p style={{ margin: '5px 0 0', fontSize: '14px' }}>Create an account to accept the invitation.</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          {/* Full Name and Phone */}
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="name" className="form-label">Full Name <span className="required">*</span></label>
              <input id="name" name="name" type="text" required value={formData.name} onChange={handleChange} className="form-input" placeholder="John Doe" disabled={loading} />
            </div>
            <div className="form-group">
              <label htmlFor="phone" className="form-label">Phone Number</label>
              <input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} className="form-input" placeholder="+1 234 567 8900" disabled={loading} />
            </div>
          </div>

          {/* Company and Job Title */}
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="company" className="form-label">Company Name</label>
              <input id="company" name="company" type="text" value={formData.company} onChange={handleChange} className="form-input" placeholder="Acme Inc." disabled={loading} />
            </div>
            <div className="form-group">
              <label htmlFor="jobTitle" className="form-label">Job Title</label>
              <input id="jobTitle" name="jobTitle" type="text" value={formData.jobTitle} onChange={handleChange} className="form-input" placeholder="Marketing Manager" disabled={loading} />
            </div>
          </div>

          {/* Industry and Company Size */}
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="industry" className="form-label">Industry</label>
              <select id="industry" name="industry" value={formData.industry} onChange={handleChange} className="form-select" disabled={loading}>
                <option value="technology">Technology</option>
                <option value="ecommerce">E-commerce</option>
                <option value="healthcare">Healthcare</option>
                <option value="education">Education</option>
                <option value="finance">Finance</option>
                <option value="realestate">Real Estate</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="companySize" className="form-label">Company Size</label>
              <select id="companySize" name="companySize" value={formData.companySize} onChange={handleChange} className="form-select" disabled={loading}>
                <option value="1-10">1-10 employees</option>
                <option value="11-50">11-50 employees</option>
                <option value="51-200">51-200 employees</option>
                <option value="201-500">201-500 employees</option>
                <option value="500+">500+ employees</option>
              </select>
            </div>
          </div>

          {/* Email Field */}
          <div className="form-group">
            <label htmlFor="email" className="form-label">Email Address <span className="required">*</span></label>
            <input id="email" name="email" type="email" autoComplete="email" required value={formData.email} onChange={handleChange} className="form-input" placeholder="you@company.com" disabled={loading} />
            <EmailCheck email={formData.email} onStatusChange={setIsEmailAvailable} onChecking={setCheckingEmail} />
          </div>

          {/* Password Field */}
          <div className="form-group">
            <label htmlFor="password" className="form-label">Password <span className="required">*</span></label>
            <div className="password-input-wrapper">
              <input id="password" name="password" type={showPassword ? "text" : "password"} autoComplete="new-password" required value={formData.password} onChange={handleChange} className="form-input password-input" placeholder="Create a password" minLength={8} disabled={loading} />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="password-toggle" disabled={loading}>{showPassword ? '👁️' : '👁️‍🗨️'}</button>
            </div>
            
            <div className="password-strength">
              <div className="strength-bar-container">
                <div className="strength-bar">
                  <div className={`strength-fill ${getPasswordStrengthColor()}`} style={{ width: `${(passwordStrength / 4) * 100}%` }}></div>
                </div>
                <span className="strength-text">{getPasswordStrengthText()}</span>
              </div>
              <ul className="password-requirements">
                <li className={formData.password.length >= 8 ? 'requirement-met' : ''}>✓ At least 8 characters</li>
                <li className={/[A-Z]/.test(formData.password) ? 'requirement-met' : ''}>✓ Uppercase letter</li>
                <li className={/[0-9]/.test(formData.password) ? 'requirement-met' : ''}>✓ Number</li>
                <li className={/[^A-Za-z0-9]/.test(formData.password) ? 'requirement-met' : ''}>✓ Special character</li>
              </ul>
            </div>
          </div>

          {/* Confirm Password */}
          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">Confirm Password <span className="required">*</span></label>
            <input id="confirmPassword" name="confirmPassword" type={showPassword ? "text" : "password"} required value={formData.confirmPassword} onChange={handleChange} className="form-input" placeholder="Re-enter your password" minLength={8} disabled={loading} />
            {formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword && <p className="error-message">Passwords do not match</p>}
          </div>

          {/* Email Error */}
          {!isEmailAvailable && !checkingEmail && (
            <div className="email-error-message">
              This email is already registered. Please{' '}
              <Link to="/signin" className="inline-link">sign in</Link> instead.
            </div>
          )}

          {/* Submit Button */}
          <button type="submit" disabled={loading || (!isEmailAvailable || checkingEmail)} className="submit-button">
            {loading ? (
              <span className="button-loading">
                <svg className="spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="spinner-circle" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="spinner-path" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </span>
            ) : 'Create Account & Verify Email'}
          </button>

          <p className="verification-note">We'll send a verification code to your email</p>
        </form>
        <div className="mode-toggle">
          <p className="mode-toggle-text">
            Already have an account?{' '}
            <Link to="/signin" className="mode-toggle-link">Sign in</Link>
          </p>
        </div>

        <p className="legal-text">By continuing, you agree to our <Link to="/terms" className="legal-link">Terms of Service</Link> and <Link to="/privacy" className="legal-link">Privacy Policy</Link></p>
      </div>
    </div>
  )
}